import Shader from "_src/engine/shader";
import extensions from "_src/engine/extensions";
import compat from "_src/engine/compat";
import { shadingType } from "_src/constants";
import { blendFactor, uniformType, uniformSource, cullingFrontFace } from "_src/engine/constants";
import { vec3, mat4 } from "_src/libs/gl-matrix";
import Uniform from "_src/engine/uniform";
import gl_types from "_src/engine/gl_types";

import { drawMode } from "_src/engine/constants";
import vertText from "_src/shaders/pbr/pbr.glslv";
import fragText from "_src/shaders/pbr/pbr.glslf";

const _vec3Tmp = vec3.create();

const preprocessorRules = {
  "FLAT_SHADING": 0,
  "SMOOTH_SHADING": 1,
  "CCW": 0,
  "CW": 1,
  "TRIANGLES_MODE": 0,
  "POINTS_MODE": 1,
  "SPOTS": 0,
  "ENV": 1,
  "COMBINED": 2,
};

export default class PBRShader extends Shader {

  constructor(params = {}) {
    super(params);
    this.textureLodSamplingEnabled = true;
    this.attachUniforms(params);
    this.setShading(params.shading !== undefined ? params["shading"] : shadingType.FLAT);
  }

  //reimplementation
  initDirectives(params) {
    this._directives["WIREFRAME"] = params.wireframe !== undefined ? params.wireframe : false;
    this._directives["FRONT_FACE"] = this.cullingFrontFace === gl_types.getFrontFace(cullingFrontFace.CLOCK_WISE) ? "CW" : "CCW";
    this._directives["INVERT_FACES"] = false;
    this._directives["SHADING"] = "";
    this._directives["DRAW_MODE"] = "";
    this._directives["NUM_LIGHTS"] = 0;
    this._directives["LIGHT_SOURCE"] = "ENV";

    this._dependentDirectives.add("WIREFRAME");
    this._dependentDirectives.add("FRONT_FACE");
    this._dependentDirectives.add("SHADING");
    this._dependentDirectives.add("DRAW_MODE");
    this._dependentDirectives.add("NUM_LIGHTS");
  }

  compile() {

    this.standardDerivativesEnabled = this._directives["SHADING"] === "FLAT_SHADING";
    this.mrtEnabled = this._directives["WIREFRAME"];

    const fragModifiedText = this.preprocessFShaderText(fragText);

    super.compile(vertText, fragModifiedText, preprocessorRules);

  }

  preprocessFShaderText(shaderText) {

    const useMRT = this.mrtEnabled && compat.mrtEnabled;

    if (!useMRT) {
      return shaderText;
    }

    const fragOutRegex = new RegExp("FRAG_OUT", "g");
    const fragDefRegex = new RegExp("FRAG_DEF_OUT", "g");

    let outputText = shaderText;

    let outputCounter = 0;
    let outputIndex = 0;
    const outputReplacer = (match) => {

      let result = "";

      switch(outputCounter) {
        case 0:
          result = useMRT ? match + outputIndex++ : match;
          break;
        case 1:
          result = this._directives["WIREFRAME"] ? match + outputIndex++ : "";
          break;
        default:
          result =  match + outputIndex++;
          break;
      }

      ++outputCounter;

      return result;

    };

    outputText = outputText.replace(fragDefRegex, outputReplacer);
    outputCounter = 0;
    outputIndex = 0;
    outputText = outputText.replace(fragOutRegex, outputReplacer);

    return outputText;

  }

  updateObjectUniformValues(object) {
    mat4.copy(this.uniforms["u_model_matrix"].value, object.modelMatrix);
  }

  updateSceneUniformValues(scene) {

    const camera = scene.camera;

    for (let i = 0; i < scene.lightPositions.length; ++i) {
      this.uniforms["u_light_positions[0]"].value[i] = scene.lightPositions[i];
      this.uniforms["u_light_colors[0]"].value[i] = scene.lightColors[i];
    }

    mat4.copy(this.uniforms["u_view_proj_matrix"].value, camera.viewProjMat);
    mat4.copy(this.uniforms["u_view_mat"].value, camera.viewMat);
    vec3.copy(this.uniforms["u_cam_pos"].value, camera.trans);
  }

  updateSceneDirectives(scene) {
    // NOTE: Light number is not changing in the app right now but it is allowed
    if (this.getNumLights() !== scene.getLightsCount()) {
      this.setNumLights(scene.getLightsCount());
    }
  }

  attachUniforms(params) {
    const numLights            = this._directives["NUM_LIGHTS"];
    const pointSize            = "pointSize" in params ? params.pointSize : 1.0;
    const backfaceColor        = vec3.fromValues(0.8, 0.8, 0.4);
    const lightColors          = new Float32Array(3 * numLights);
    const lightPositions       = new Float32Array(3 * numLights);
    const viewProjectionMatrix = mat4.create();
    const viewInverseMatrix    = mat4.create();
    const cameraPosition       = vec3.create();
    const modelMatrix          = mat4.create();

    this.uniforms["u_backface_color"]          = new Uniform(uniformType.FLOAT3, backfaceColor);
    this.uniforms["u_point_size"]              = new Uniform(uniformType.FLOAT1,  pointSize);
    this.uniforms["u_light_colors[0]"]         = new Uniform(uniformType.FLOAT3V, lightColors, uniformSource.SCENE);
    this.uniforms["u_light_positions[0]"]      = new Uniform(uniformType.FLOAT3V, lightPositions, uniformSource.SCENE);
    this.uniforms["u_view_proj_matrix"]        = new Uniform(uniformType.MAT4, viewProjectionMatrix, uniformSource.SCENE);
    this.uniforms["u_view_mat"]                = new Uniform(uniformType.MAT4, viewInverseMatrix, uniformSource.SCENE);
    this.uniforms["u_cam_pos"]                 = new Uniform(uniformType.FLOAT3, cameraPosition, uniformSource.SCENE);
    this.uniforms["u_model_matrix"]            = new Uniform(uniformType.MAT4, modelMatrix, uniformSource.OBJECT);
    this.uniforms["tDiffuse"]                  = new Uniform(uniformType.INT1, 0, uniformSource.OBJECT);
    this.uniforms["tRoughnessMetallic"]        = new Uniform(uniformType.INT1, 1, uniformSource.OBJECT);
    this.uniforms["tIrradianceMap"]            = new Uniform(uniformType.INT1, 2, uniformSource.OBJECT);
    this.uniforms["tPrefilterMap"]             = new Uniform(uniformType.INT1, 3, uniformSource.OBJECT);
    this.uniforms["tBRDF"]                     = new Uniform(uniformType.INT1, 4, uniformSource.OBJECT);
  }

  setPointSize(size) {
    this.setUniform("u_point_size", size);
  }

  setWireframeEnabled(enabled, transparent) {
    this._setDirective("WIREFRAME", enabled);

    if (enabled && transparent) {
      this.setSourceBlendFactor(blendFactor.SRC_ALPHA);
      this.setDestBlendFactor(blendFactor.ONE_MINUS_SRC_ALPHA);
    }
  }

  setCullingFrontFace(frontFace) {
    super.setCullingFrontFace(frontFace);
    this._setDirective("FRONT_FACE", frontFace === cullingFrontFace.CLOCK_WISE ? "CW" : "CCW")
  }

  setShading(shading) {
    const oldShading = this._directives["SHADING"];

    const standardDerivatives = extensions.get("OES_standard_derivatives");
    if (shading === shadingType.FLAT && standardDerivatives) {
      this._setDirective("SHADING","FLAT_SHADING");
    } else {
      this._setDirective("SHADING","SMOOTH_SHADING");
    }

    return oldShading === this._directives["SHADING"];
  }

  setDrawMode(mode) {
    switch (mode) {
      case drawMode.POINTS:
        this._setDirective("DRAW_MODE","POINTS_MODE");
        break;
      default:
        this._setDirective("DRAW_MODE","TRIANGLES_MODE");
    }
  }

  setNumLights(numLights) {
    this._setDirective("NUM_LIGHTS", numLights);
    if (this.uniforms["u_light_positions[0]"].value.length != numLights) {
      this.uniforms["u_light_positions[0]"].value = new Float32Array(3 * numLights);
      this.uniforms["u_light_colors[0]"].value = new Float32Array(3 * numLights);
    }
  }

  getNumLights() {
    return this._directives["NUM_LIGHTS"];
  }

  setBackFaceColor(r, g, b) {
    const color = vec3.set(_vec3Tmp, r, g, b);
    this.setUniform("u_backface_color", color);
  }
}
